import React, { useState, useEffect } from 'react'
import {
  BanknotesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import { pluggyAPI, type PluggyConnection, type PluggyAccount, type PluggySettingsCreate } from '@/services/api'
import PluggyConnectWidget from '@/components/integrations/PluggyConnectWidget'

interface ConnectionWithAccounts extends PluggyConnection {
  accounts?: PluggyAccount[]
  total_balance?: number
}

const PluggyDashboard: React.FC = () => {
  const [connections, setConnections] = useState<ConnectionWithAccounts[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState<number | null>(null)

  const [settingsForm, setSettingsForm] = useState<PluggySettingsCreate>({
    client_id: '',
    client_secret: '',
    environment: 'sandbox'
  })
  const [savingSettings, setSavingSettings] = useState(false)
  const [testingSettings, setTestingSettings] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  // Estados para o Pluggy Widget
  const [connectToken, setConnectToken] = useState<string | null>(null)
  const [showWidget, setShowWidget] = useState(false)
  const [connecting, setConnecting] = useState(false)

  useEffect(() => {
    fetchConnections()
  }, [])

  const fetchConnections = async () => {
    try {
      setLoading(true)
      const conns = await pluggyAPI.getConnections()

      // Buscar contas de cada conexão
      const connsWithAccounts = await Promise.all(
        conns.map(async (conn) => {
          try {
            const accounts = await pluggyAPI.getConnectionAccounts(conn.id)
            const total_balance = accounts.reduce((sum, acc) => {
              const balance = acc.balance?.current ?? acc.balance ?? 0
              const numBalance = typeof balance === 'number' ? balance : parseFloat(balance) || 0
              return sum + (isNaN(numBalance) ? 0 : numBalance)
            }, 0)
            return { ...conn, accounts, total_balance }
          } catch (error) {
            return { ...conn, accounts: [], total_balance: 0 }
          }
        })
      )

      setConnections(connsWithAccounts)
    } catch (error: any) {
      console.error('Erro ao carregar conexões:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSync = async (connectionId: number) => {
    try {
      setSyncing(connectionId)
      await pluggyAPI.syncConnection(connectionId)
      await fetchConnections()
    } catch (error: any) {
      alert('Erro ao sincronizar: ' + (error.response?.data?.detail || error.message))
    } finally {
      setSyncing(null)
    }
  }

  const handleDelete = async (connectionId: number) => {
    if (!confirm('Deseja realmente desconectar este banco?')) return

    try {
      await pluggyAPI.deleteConnection(connectionId)
      await fetchConnections()
    } catch (error: any) {
      alert('Erro ao deletar conexão: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleSaveSettings = async () => {
    if (!settingsForm.client_id || !settingsForm.client_secret) {
      alert('Preencha Client ID e Client Secret')
      return
    }

    try {
      setSavingSettings(true)
      setTestResult(null)

      // Salvar credenciais
      await pluggyAPI.saveSettings(settingsForm)

      // Testar conexão automaticamente
      setTestingSettings(true)
      const testResponse = await pluggyAPI.testSettings()
      setTestResult(testResponse)

      if (testResponse.success) {
        // Limpar formulário
        setSettingsForm({
          client_id: '',
          client_secret: '',
          environment: 'sandbox'
        })
      }
    } catch (error: any) {
      alert('Erro ao salvar credenciais: ' + (error.response?.data?.detail || error.message))
    } finally {
      setSavingSettings(false)
      setTestingSettings(false)
    }
  }

  // 🆕 Abre widget Pluggy diretamente (sem modal de seleção)
  const handleConnectDirectly = async () => {
    try {
      setConnecting(true)

      console.log('🏦 Criando connect token...')

      // 1. Criar connect_token SEM connector específico (widget mostra todos os bancos)
      const tokenData = await pluggyAPI.createConnectToken()
      console.log('✓ Connect token criado:', tokenData.connect_token.substring(0, 20) + '...')

      // 2. Armazenar token
      setConnectToken(tokenData.connect_token)

      // 3. Abrir widget diretamente
      setShowWidget(true)
    } catch (error: any) {
      console.error('✗ Erro ao criar connect token:', error)
      alert('Erro ao preparar conexão: ' + (error.response?.data?.detail || error.message))
      setConnecting(false)
    }
  }

  // 🆕 Callback de sucesso do widget
  const handleWidgetSuccess = async (data: { item: { id: string } }) => {
    try {
      console.log('✓ Banco conectado! Item ID:', data.item.id)

      // Salvar conexão no backend (connector_id será extraído do item)
      const connection = await pluggyAPI.createConnection({
        item_id: data.item.id,
        auto_sync: true,
        sync_frequency: 1440, // 24h
        webhook_url: null
      })

      console.log('✓ Conexão salva:', connection)

      // Sincronizar automaticamente
      console.log('🔄 Iniciando sincronização automática...')
      await pluggyAPI.syncConnection(connection.id)
      console.log('✓ Sincronização concluída')

      // Atualizar lista
      await fetchConnections()

      // Feedback
      alert(`✓ Banco conectado com sucesso! Transações sincronizadas.`)
    } catch (error: any) {
      console.error('✗ Erro ao salvar conexão:', error)
      alert('Erro ao salvar conexão: ' + (error.response?.data?.detail || error.message))
    } finally {
      setShowWidget(false)
      setConnectToken(null)
      setConnecting(false)
    }
  }

  // 🆕 Callback de erro do widget
  const handleWidgetError = (error: { message: string; code?: string }) => {
    console.error('✗ Erro no widget Pluggy:', error)
    alert('Erro ao conectar banco: ' + (error.message || 'Erro desconhecido'))
    setShowWidget(false)
    setConnectToken(null)
    setConnecting(false)
  }

  // 🆕 Callback de fechamento do widget
  const handleWidgetClose = () => {
    console.log('○ Widget fechado pelo usuário')
    setShowWidget(false)
    setConnectToken(null)
    setConnecting(false)
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, text: 'Ativa' },
      updating: { color: 'bg-blue-100 text-blue-800', icon: ArrowPathIcon, text: 'Atualizando' },
      login_error: { color: 'bg-red-100 text-red-800', icon: ExclamationCircleIcon, text: 'Erro de login' },
      error: { color: 'bg-red-100 text-red-800', icon: ExclamationCircleIcon, text: 'Erro' },
      outdated: { color: 'bg-yellow-100 text-yellow-800', icon: ExclamationCircleIcon, text: 'Desatualizada' }
    }

    const badge = badges[status as keyof typeof badges] || badges.error
    const Icon = badge.icon

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    )
  }

  const formatCurrency = (value: number | null | undefined) => {
    const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(safeValue)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Nunca sincronizado'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'agora'
    if (diffMins < 60) return `há ${diffMins} min`
    if (diffHours < 24) return `há ${diffHours}h`
    if (diffDays < 7) return `há ${diffDays}d`
    return date.toLocaleDateString('pt-BR')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (connections.length === 0) {
    return (
      <>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center bg-white rounded-xl border-2 border-dashed border-gray-300 p-12">
            <BanknotesIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhum banco conectado
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Conecte suas contas bancárias para sincronizar automaticamente transações e saldos.
            </p>
            <button
              onClick={handleConnectDirectly}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
            >
              <PlusIcon className="w-5 h-5" />
              Conectar Primeiro Banco
            </button>
          </div>
        </div>

        {/* Widget renderizado diretamente - Pluggy SDK cria próprio modal full-screen */}
        {showWidget && connectToken && (
          <PluggyConnectWidget
            connectToken={connectToken}
            includeSandbox={settingsForm.environment === 'sandbox'}
            onSuccess={handleWidgetSuccess}
            onError={handleWidgetError}
            onClose={handleWidgetClose}
          />
        )}
      </>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bancos Conectados</h1>
          <p className="text-gray-600 mt-1">
            {connections.length} {connections.length === 1 ? 'banco conectado' : 'bancos conectados'}
          </p>
        </div>
        <button
          onClick={handleConnectDirectly}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
        >
          <PlusIcon className="w-5 h-5" />
          Conectar Banco
        </button>
      </div>

      {/* Connections Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {connections.map((connection) => (
          <div
            key={connection.id}
            className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
          >
            {/* Bank Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {connection.connector_name ? (
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <BanknotesIcon className="w-6 h-6 text-indigo-600" />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <BanknotesIcon className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {connection.connector_name || 'Banco'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Conectado em {new Date(connection.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
              {getStatusBadge(connection.status)}
            </div>

            {/* Balance */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600 mb-1">Saldo Total</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(connection.total_balance || 0)}
              </p>
              {connection.accounts && connection.accounts.length > 0 && (
                <div className="mt-3 space-y-1">
                  {connection.accounts.map((account) => {
                    const balance = account.balance?.current ?? account.balance ?? 0
                    const numBalance = typeof balance === 'number' ? balance : parseFloat(balance as any) || 0
                    return (
                      <div key={account.id} className="flex justify-between text-sm">
                        <span className="text-gray-600">{account.name}</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(numBalance)}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Sync Info */}
            <div className="flex items-center justify-between text-sm mb-4">
              <span className="text-gray-600">
                Última sinc: {formatDate(connection.last_sync)}
              </span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSync(connection.id)}
                disabled={syncing === connection.id}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 font-medium disabled:opacity-50"
              >
                <ArrowPathIcon className={`w-4 h-4 ${syncing === connection.id ? 'animate-spin' : ''}`} />
                {syncing === connection.id ? 'Sincronizando...' : 'Sincronizar'}
              </button>
              <button
                onClick={() => handleDelete(connection.id)}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                title="Desconectar"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Widget renderizado diretamente - Pluggy SDK cria próprio modal full-screen */}
      {showWidget && connectToken && (
        <PluggyConnectWidget
          connectToken={connectToken}
          includeSandbox={settingsForm.environment === 'sandbox'}
          onSuccess={handleWidgetSuccess}
          onError={handleWidgetError}
          onClose={handleWidgetClose}
        />
      )}
    </div>
  )
}

export default PluggyDashboard
